import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';
import {
  AddIconComponent,
  CloseSquareIconComponent,
  DeleteIconComponent,
  TickSquareIconComponent,
} from '@kuiper/icons';
import { Department } from '../department-model';
import { DepartmentService } from '../department.service';
import { ACTIONS_INDEXES, ACTIONS_MENU_ITEMS } from './departments.constant';
import {
  DEPARTMENTS_COLUMN_IDENTIFIERS,
  DEPARTMENTS_COLUMNS_SORT,
  DEPARTMENTS_FILTER_TABS,
} from './departments-flat-list.constant';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { FilteredTableComponent } from '../../../../../common/components/filtered-table/filtered-table.component';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { DEFAULT_PAGE_SIZE } from '../../../../../common/constants/pagination.constants';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import {
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { TruncatePipe } from '../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import {
  ApiResponse,
  Meta,
} from '../../../../../common/models/api-response.model';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { FilteredTablePayload } from '../../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-departments-flat-list',
  imports: [
    FilteredTableComponent,
    TranslocoPipe,
    BadgeComponent,
    TruncatePipe,
    AvatarPipe,
    ActionListComponent,
    RouterModule,
    AddIconComponent,
    CloseSquareIconComponent,
    DeleteIconComponent,
    TickSquareIconComponent,
    TenantPathPipe,
    UserStatusPipe,
  ],
  templateUrl: './departments-flat-list.component.html',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentsFlatListComponent {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  readonly DEPARTMENTS_COLUMN_IDENTIFIERS = DEPARTMENTS_COLUMN_IDENTIFIERS;

  private readonly router = inject(Router);
  private readonly departmentService = inject(DepartmentService);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  activateModal = viewChild<TemplateRef<unknown>>('activate');
  deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');

  readonly isDeleteInactiveEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .delete_inactive_groups_departments_job_titles_and_positions
    )
  ).asReadonly();

  isLoading = signal<boolean>(false);
  departmentList = signal<Department[]>([]);
  meta = signal<Meta | undefined>(undefined);
  columns = signal<TableColumns[]>(
    DEPARTMENTS_COLUMNS_SORT.map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    DEPARTMENTS_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  selectedDepartment = signal<Department | undefined>(undefined);
  isDepartmentStateLoading = signal<boolean>(false);

  actionItemsMapped = signal<ActionList[]>(
    ACTIONS_MENU_ITEMS.map(item => ({
      ...item,
      label: this.transloco.translate(item.label),
    }))
  );

  actionMenuItems = computed(() => {
    const department = this.selectedDepartment();
    const actionItemsMapped = this.actionItemsMapped();

    if (!department) {
      return [];
    }

    const actionItems: ActionList[] = [
      {
        ...actionItemsMapped[ACTIONS_INDEXES.VIEW],
        routerLinks: [
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${department.id.toString()}`
          ),
        ],
      },
      {
        ...actionItemsMapped[ACTIONS_INDEXES.EDIT],
        hasDivider: true,
        routerLinks: [
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.DEPARTMENTS.FORM}/${department.id.toString()}`
          ),
        ],
      },
    ];

    if (department.state?.value === 'active') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DEACTIVATE]);
    } else if (department.state?.value === 'inactive') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.ACTIVATE]);
    }

    if (
      this.isDeleteInactiveEnabled() &&
      department.state?.value === 'inactive'
    ) {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DELETE]);
    }

    return actionItems;
  });

  getDepartmentList$ = () => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, tab } = this.payload();

    return this.departmentService
      .getDepartmentList(page, size, sortColumn, tab?.key)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  getDepartmentList() {
    this.getDepartmentList$().subscribe({
      next: (res: ApiResponse<Department>) =>
        this.handleDepartmentListSuccess(res),
    });
  }

  handleDepartmentListSuccess(res: ApiResponse<Department>) {
    this.departmentList.set(res.data);
    this.meta.set(res.meta);
  }

  handleAction($event: string, row: Department) {
    this.selectedDepartment.set(row);

    switch ($event) {
      case 'view':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.DETAILS}`),
          row.id,
        ]);
        break;
      case 'edit':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.FORM}`),
          row.id,
        ]);
        break;
      case 'deactivate':
        this.openModal('deactivate');
        break;
      case 'activate':
        this.openModal('activate');
        break;
      case 'delete':
        this.openModal('delete');
        break;
    }
  }

  openModal(type: 'activate' | 'deactivate' | 'delete') {
    const customizeContent =
      type === 'deactivate'
        ? this.deactivateModal()
        : type === 'activate'
          ? this.activateModal()
          : this.deleteModal();

    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent,
      width: '24',
    });
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deactivateDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.deactivateDepartment(departmentId)
    );
  }

  activateDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.activateDepartment(departmentId)
    );
  }

  deleteDepartment() {
    this.updateDepartmentState(departmentId =>
      this.departmentService.deleteDepartment(departmentId)
    );
  }

  updateDepartmentState(
    request: (departmentId: string) => Observable<unknown>
  ) {
    const departmentId = this.selectedDepartment()?.id;
    if (!departmentId) {
      return;
    }

    this.isDepartmentStateLoading.set(true);
    request(departmentId.toString())
      .pipe(
        finalize(() => this.isDepartmentStateLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.getDepartmentList();
          this.closeModal();
        },
        error: (error: { message?: string }) => {
          this.toaster.add(error.message ?? '', '', '', 'error', true, true);
        },
      });
  }

  viewUserDetails(department: Department) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${department.id}`
      )
    );
  }
}
